import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, FileText, AlertTriangle } from "lucide-react";
import Footer from "../components/sinpe/Footer";

const Section = ({ number, title, children }) => (
    <section className="mb-10" data-testid={`terms-section-${number}`}>
        <h2 className="font-display text-2xl md:text-3xl font-medium text-[#022C22] tracking-tight mb-4">
            <span className="text-[#10B981] font-mono mr-3 text-xl">{String(number).padStart(2, "0")}</span>
            {title}
        </h2>
        <div className="space-y-4 text-[#334155] text-base leading-relaxed">{children}</div>
    </section>
);

export default function Terms() {
    const updatedAt = "2 de junio de 2026";

    return (
        <div className="min-h-screen bg-white" data-testid="terms-page">
            <header className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0]">
                <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-home-link">
                        <div className="h-9 w-9 rounded-xl bg-[#064E3B] flex items-center justify-center shadow-[0_4px_12px_rgba(6,78,59,0.25)] group-hover:scale-105 transition-transform">
                            <ShieldCheck className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex flex-col leading-none">
                            <span className="font-display font-semibold text-[#022C22] text-base tracking-tight">SINPE-Flow</span>
                            <span className="text-[10px] uppercase tracking-widest text-[#64748B]">Pagos verificados</span>
                        </div>
                    </Link>
                    <Link to="/" className="btn-ghost flex items-center gap-2" data-testid="back-home">
                        <ArrowLeft className="w-4 h-4" /> Volver al inicio
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D1FAE5] text-[#065F46] text-xs font-medium tracking-wide mb-5">
                    <FileText className="w-3.5 h-3.5" />
                    Documento legal
                </div>
                <h1 className="font-display text-4xl md:text-6xl font-semibold tracking-tighter leading-[1.05] text-[#022C22]" data-testid="terms-title">
                    Términos y Condiciones de Servicio
                </h1>
                <p className="mt-5 text-lg text-[#334155]">
                    Términos de uso de SINPE-Flow, una herramienta de verificación de pagos SINPE Móvil para vendedores en Costa Rica.
                </p>
                <p className="mt-2 text-sm uppercase tracking-widest text-[#64748B]">
                    Última actualización: {updatedAt}
                </p>

                {/* Highlight card */}
                <div className="mt-10 rounded-2xl border border-[#10B981] bg-[#D1FAE5] p-6 flex gap-4" data-testid="terms-highlight">
                    <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-5 h-5 text-[#065F46]" />
                    </div>
                    <div>
                        <p className="font-display text-lg font-medium text-[#022C22] leading-snug">
                            Importante: somos un validador de datos, no una pasarela ni un custodio de fondos.
                        </p>
                        <p className="mt-2 text-sm text-[#065F46]">
                            SINPE-Flow no recibe, retiene, transfiere ni custodia dinero. Tu transferencia SINPE Móvil ocurre siempre entre tu banco y el banco del vendedor. Nosotros únicamente procesamos la información de los comprobantes para confirmar si la transacción fue realizada.
                        </p>
                    </div>
                </div>

                <div className="mt-16">
                    <Section number={1} title="Naturaleza del servicio">
                        <p>
                            SINPE-Flow (en adelante, "el Servicio") es una herramienta de software que permite a comerciantes y vendedores ("el Vendedor") verificar el estado de transferencias SINPE Móvil ejecutadas por sus clientes ("el Cliente") mediante la lectura y procesamiento inteligente de comprobantes de pago.
                        </p>
                        <p>
                            El Servicio funciona como un <strong>validador de datos</strong>: extrae la información de las imágenes de los comprobantes enviados por los Clientes y registra el resultado en la plataforma del Vendedor.
                        </p>
                        <p>
                            <strong>SINPE-Flow no es una pasarela de pago, no es una entidad financiera, no es un agente de retención y no presta servicios de remesas ni de custodia.</strong> No tenemos cuentas bancarias intermediarias ni billeteras digitales que reciban dinero de los Clientes.
                        </p>
                    </Section>

                    <Section number={2} title="Flujo de la transacción">
                        <p>El proceso de una transacción se ejecuta de la siguiente manera:</p>
                        <ol className="list-decimal pl-6 space-y-2">
                            <li>El Cliente realiza una transferencia SINPE Móvil directamente desde su banco al número telefónico del Vendedor.</li>
                            <li>El Cliente envía la imagen del comprobante de la transferencia al canal oficial de WhatsApp del Vendedor (operado y automatizado a través de la infraestructura de SINPE-Flow).</li>
                            <li>El Servicio procesa la imagen, extrae los datos de la transacción mediante inteligencia artificial y registra el resultado.</li>
                            <li>El Vendedor recibe la confirmación del estado en su Dashboard administrativo.</li>
                        </ol>
                        <p>
                            <strong>En ningún punto de este flujo SINPE-Flow recibe, mueve, retiene ni custodia fondos.</strong> El dinero se mueve únicamente entre las cuentas bancarias del Cliente y del Vendedor.
                        </p>
                    </Section>

                    <Section number={3} title="Aceptación de los términos">
                        <p>
                            Al crear una cuenta de Vendedor en SINPE-Flow, al utilizar el canal de WhatsApp para reportar un pago como Cliente, o al integrar el Servicio en tu negocio, aceptás estos Términos y nuestra Política de Privacidad. Si no estás de acuerdo, debés abstenerte de usar el Servicio.
                        </p>
                    </Section>

                    <Section number={4} title="Cuenta del Vendedor">
                        <p>
                            La cuenta de Vendedor se crea mediante autenticación con Google. El Vendedor es responsable de:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Mantener la confidencialidad de su cuenta Google asociada.</li>
                            <li>Verificar la veracidad de su número SINPE Móvil registrado en el perfil.</li>
                            <li>Configurar correctamente su nombre de negocio, slug público y preferencias de notificación.</li>
                            <li>Conciliar las transacciones verificadas con los movimientos reales de su cuenta bancaria.</li>
                        </ul>
                    </Section>

                    <Section number={5} title="Uso del Servicio y Límites de los Planes">
                        <p>
                            El Servicio ofrece un acceso inicial gratuito limitado a un máximo de <strong>20 transacciones verificadas</strong> ("Plan Básico").
                        </p>
                        <p>
                            Una vez alcanzado o agotado este límite de 20 transacciones, el procesamiento automático de imágenes a través de WhatsApp se pausará. Para reactivar el servicio, el Vendedor deberá adquirir la suscripción correspondiente ("Plan Premium"). El Servicio se reserva el derecho de suspender la validación automática de manera inmediata si el límite es alcanzado y no se registra la renovación o el pago del plan respectivo.
                        </p>
                    </Section>

                    <Section number={6} title="Limitación de responsabilidad">
                        <p>
                            El Servicio se entrega "tal cual" ("as is"). SINPE-Flow no garantiza:
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Que el sistema de mensajería de WhatsApp o la infraestructura de red respondan en todo momento o sin demoras.</li>
                            <li>Que una verificación en el Dashboard garantice la disponibilidad inmediata del dinero en la cuenta del Vendedor (los tiempos de acreditación finales los define el banco, no SINPE-Flow).</li>
                            <li>Que ante cualquier duda, el Vendedor deba abstenerse de entregar bienes o servicios de alto valor hasta confirmar el movimiento directo en su plataforma bancaria.</li>
                        </ul>
                        <p>
                            <strong>SINPE-Flow no es responsable por pérdidas, daños o reclamos derivados de transacciones cuyo dinero no llegó a la cuenta del Vendedor o por imágenes de comprobantes falsificados que alteren la percepción del negocio.</strong> La responsabilidad de cobrar y conciliar siempre recae en el Vendedor.
                        </p>
                    </Section>

                    <Section number={7} title="Protección de datos personales">
                        <p>
                            Almacenamos únicamente los datos mínimos necesarios para operar el Servicio: correo electrónico, nombre, foto de perfil de Google, número SINPE Móvil, nombre de negocio, slug y los datos extraídos de las transacciones consultadas. No vendemos ni compartimos estos datos con terceros con fines publicitarios.
                        </p>
                        <p>
                            Cumplimos con la Ley N.º 8968 de Protección de la Persona Frente al Tratamiento de sus Datos Personal de Costa Rica. Cualquier solicitud de acceso, rectificación o eliminación de datos se puede ejercer escribiendo a <span className="font-mono">hola@sinpeflow.com</span>.
                        </p>
                    </Section>

                    <Section number={8} title="Suspensión y cancelación">
                        <p>
                            Nos reservamos el derecho de suspender o cancelar la cuenta de un Vendedor que utilice el Servicio para actividades fraudulentas, ilícitas o que violen estos Términos. El Vendedor también puede solicitar la baja de su cuenta en cualquier momento.
                        </p>
                    </Section>

                    <Section number={9} title="Modificaciones">
                        <p>
                            Podemos actualizar estos Términos cuando sea necesario. La fecha de la última actualización se muestra al inicio del documento. El uso continuado del Servicio después de una actualización implica la aceptación de los nuevos Términos.
                        </p>
                    </Section>

                    <Section number={10} title="Jurisdicción y legislación aplicable">
                        <p>
                            Estos Términos se rigen por las leyes de la República de Costa Rica. Cualquier controversia derivada del uso del Servicio se someterá a los tribunales competentes de San José, Costa Rica.
                        </p>
                    </Section>

                    <Section number={11} title="Contacto">
                        <p>
                            Para cualquier consulta sobre estos Términos, escribinos a <span className="font-mono">hola@sinpeflow.com</span>.
                        </p>
                    </Section>
                </div>

                <div className="mt-16 pt-10 border-t border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm text-[#64748B]">
                    <p>Documento vigente desde {updatedAt}.</p>
                    <Link to="/" className="btn-secondary text-sm" data-testid="bottom-back-home">
                        <ArrowLeft className="w-4 h-4" /> Volver al inicio
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
}
